import React, { useState, useMemo } from "react";
import { articles } from "../mock/mock";
import { Link } from "react-router-dom";
import { ArrowRight, Clock } from "lucide-react";

const categories = ["All", ...Array.from(new Set(articles.map((a) => a.category)))];

const Articles = () => {
  const [filter, setFilter] = useState("All");

  const list = useMemo(() => {
    return filter === "All" ? articles : articles.filter((a) => a.category === filter);
  }, [filter]);

  const featured = list[0];
  const rest = list.slice(1);

  return (
    <section id="insights" className="py-24 lg:py-32 bg-ivory-100/60">
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <div className="grid lg:grid-cols-12 gap-10 mb-14">
          <div className="lg:col-span-6">
            <div className="flex items-center gap-3 mb-6">
              <span className="h-px w-10 bg-bronze" />
              <span className="text-[11px] uppercase tracking-[0.3em] text-ink/60">
                Insights & Writing
              </span>
            </div>
            <h2 className="font-serif-display text-4xl lg:text-5xl text-ink leading-[1.05] font-light">
              Notes from the service leader's desk.
            </h2>
          </div>
          <div className="lg:col-span-5 lg:col-start-8 flex items-end">
            <p className="text-ink/65 leading-relaxed">
              Short essays on P&amp;L, managed services, leadership and the GCC enterprise market
              — lessons from 15+ years in the field.
            </p>
          </div>
        </div>

        <div className="flex flex-wrap gap-2 mb-12">
          {categories.map((c) => (
            <button
              key={c}
              onClick={() => setFilter(c)}
              className={`px-4 py-2 text-xs uppercase tracking-[0.18em] rounded-sm border transition-colors ${
                filter === c
                  ? "bg-ink text-ivory-50 border-ink"
                  : "bg-transparent text-ink/70 border-ink/15 hover:border-ink hover:text-ink"
              }`}
            >
              {c}
            </button>
          ))}
        </div>

        {list.length === 0 && <p className="text-ink/60">No articles in this category yet.</p>}

        {featured && (
          <Link
            to={`/insights/${featured.slug}`}
            className="lift-card block group bg-ivory-50 border border-ink/10 p-8 lg:p-12 rounded-sm mb-8"
          >
            <div className="grid lg:grid-cols-12 gap-8 items-center">
              <div className="lg:col-span-8">
                <div className="flex items-center gap-4 mb-6">
                  <span className="text-[11px] uppercase tracking-[0.22em] text-bronze font-medium">
                    Featured · {featured.category}
                  </span>
                  <span className="text-ink/30">•</span>
                  <span className="text-[11px] uppercase tracking-[0.22em] text-ink/50">
                    {featured.date}
                  </span>
                </div>
                <h3 className="font-serif-display text-3xl lg:text-5xl text-ink leading-[1.05] font-light group-hover:text-ink-700 transition-colors">
                  {featured.title}
                </h3>
                <p className="mt-6 text-ink/70 leading-relaxed text-lg max-w-2xl">
                  {featured.excerpt}
                </p>
              </div>
              <div className="lg:col-span-4 flex lg:justify-end">
                <div className="inline-flex items-center gap-3 text-sm text-ink group-hover:text-bronze transition-colors">
                  <Clock className="h-4 w-4" /> {featured.readTime}
                  <ArrowRight className="h-4 w-4 group-hover:translate-x-1 transition-transform" />
                </div>
              </div>
            </div>
          </Link>
        )}

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {rest.map((a) => (
            <Link
              to={`/insights/${a.slug}`}
              key={a.id}
              className="lift-card group bg-ivory-50 border border-ink/10 p-7 rounded-sm flex flex-col"
            >
              <div className="flex items-center justify-between mb-5">
                <span className="text-[11px] uppercase tracking-[0.22em] text-bronze font-medium">
                  {a.category}
                </span>
                <span className="text-[11px] text-ink/45">{a.date}</span>
              </div>
              <h3 className="font-serif-display text-xl text-ink leading-snug mb-3 group-hover:text-ink-700">
                {a.title}
              </h3>
              <p className="text-sm text-ink/65 leading-relaxed flex-1">{a.excerpt}</p>
              <div className="mt-6 pt-4 border-t border-ink/10 flex items-center justify-between text-xs text-ink/55">
                <span className="inline-flex items-center gap-1.5">
                  <Clock className="h-3.5 w-3.5" /> {a.readTime}
                </span>
                <ArrowRight className="h-4 w-4 text-ink/40 group-hover:text-bronze group-hover:translate-x-1 transition-all" />
              </div>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
};

export default Articles;
